/**
 * The tracking systems (M3-08).
 *
 * Split from `sign.ts` for the reason `camp.ts` is split from `needs.ts` and
 * `sky.ts` from `weather.ts`, and the reason is forced rather than aesthetic:
 * `world.ts` has to name `Signs` in order to store the table, so `sign.ts` may
 * not import anything that reaches world state — and a `System` does, through
 * `SystemContext`. Everything that is pure data and arithmetic lives there;
 * everything that reads a live world lives here.
 */
import { decaySigns, DEN_SIGN_EVERY, signKey } from './sign.js';
/**
 * The hour turning over every mark in the valley (M3-08).
 *
 * Hourly rather than per-tick, and that is the same argument the weather makes:
 * how fast a print fills in is a fact about the afternoon, not about each
 * hundred milliseconds of it, and a per-tick version would walk a table of
 * thousands of records ten times a second to subtract a number too small to
 * store.
 */
export const signDecaySystem = {
    name: 'sign-decay',
    phase: 'hour',
    run(ctx) {
        decaySigns(ctx.world.signs, ctx.world.weather);
    },
};
/**
 * Dens acquire the look of being lived in (M3-08).
 *
 * The ticket's "find dens by working back along trails", and the half that is
 * not the trail: following prints gets a hunter to the right hillside, and the
 * droppings at the mouth are what tell them they have arrived. Without it a den
 * is invisible until something happens to walk out of one while you are
 * watching, and the whole working-backwards loop has no destination.
 *
 * Ambient rather than emitted, because it is the accumulation of comings and
 * goings nobody saw — including all the ones that happened before the server
 * started.
 */
export const denSignSystem = {
    name: 'den-sign',
    phase: 'hour',
    run(ctx) {
        for (const id of Object.keys(ctx.world.dens).sort()) {
            const den = ctx.world.dens[id];
            if (!den)
                continue;
            const key = signKey(den.x, den.y, 'droppings');
            const existing = ctx.world.signs[key];
            // Refreshed on a slow clock rather than written every hour: a den should
            // read as *used*, not as though something is standing in the doorway.
            if (existing && ctx.tick - existing.at < DEN_SIGN_EVERY)
                continue;
            ctx.world.signs[key] = {
                x: den.x,
                y: den.y,
                kind: 'droppings',
                speciesId: den.speciesId,
                // A den mouth has traffic in every direction; the record needs one, and
                // which it is says nothing. Fixed rather than rolled, so it cannot
                // become a heading somebody follows into a hillside.
                direction: 's',
                at: ctx.tick,
                strength: 0.8,
            };
        }
    },
};
export const SIGN_SYSTEMS = [signDecaySystem, denSignSystem];
//# sourceMappingURL=tracking.js.map